import React, { FC, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { CentsDataGrid } from 'components/common/dataGrid/dataGrid';
import {
  buildStoreEntitlementRows,
  getHeldSlugs,
} from 'utils/entitlements/buildStoreEntitlementRows';
import { ENTITLEMENT_AUDIT_TRAIL_QUERY_KEY } from 'api/queries/useEntitlementAuditTrail';
import { useEntitlementsForBusiness } from 'api/queries/useEntitlementsForBusiness';
import { useGrantEntitlements } from 'api/queries/useGrantEntitlements';
import { useRevokeEntitlements } from 'api/queries/useRevokeEntitlements';
import {
  ENTITLEMENT_ACTIONS,
  ENTITLEMENT_LABELS,
  ENTITLEMENT_SOURCES,
} from 'constants/entitlements/entitlements';
import {
  EntitlementAction,
  EntitlementChange,
  StoreEntitlementRow,
  StoreSummary,
} from 'types/entitlements/entitlements';
import { EntitlementAuditTrail } from './EntitlementAuditTrail';
import { EntitlementChangeDialog } from './EntitlementChangeDialog';

interface EntitlementsProps {
  businessId: number;
  stores: StoreSummary[];
}

const COLUMNS: GridColDef<StoreEntitlementRow>[] = [
  { headerName: 'Store', field: 'name', flex: 1 },
  {
    headerName: 'Entitlements',
    field: 'slugs',
    flex: 1,
    sortable: false,
    renderCell: ({ row }) =>
      row.slugs.length ? (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          {row.slugs.map((slug) => (
            <Chip key={slug} label={ENTITLEMENT_LABELS[slug]} size="small" color="primary" />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="textSecondary" lineHeight="inherit">
          None
        </Typography>
      ),
  },
  {
    headerName: 'Source',
    field: 'source',
    flex: 1,
    valueFormatter: ({ value }) =>
      value === ENTITLEMENT_SOURCES.ENTITLEMENTS ? 'Entitlements' : 'Fallback (not provisioned)',
  },
];

/**
 * Platform entitlements per store, with grant and revoke (COS-4844). A grant targets stores, not the
 * business, so every action applies to the rows selected in the table.
 */
export const Entitlements: FC<EntitlementsProps> = ({ businessId, stores }) => {
  const [selectedStoreIds, setSelectedStoreIds] = useState<GridRowSelectionModel>([]);
  const [action, setAction] = useState<EntitlementAction | null>(null);
  const [changeCount, setChangeCount] = useState(0);

  const queryClient = useQueryClient();
  const { data: entitlements, isLoading, refetch } = useEntitlementsForBusiness(businessId);

  const rows = useMemo(
    () => buildStoreEntitlementRows(stores, entitlements ?? []),
    [stores, entitlements],
  );
  const selectedRows = rows.filter(({ id }) => selectedStoreIds.includes(id));
  const hasRevocableSelection = getHeldSlugs(selectedRows).length > 0;

  const handleSuccess = (): void => {
    setAction(null);
    setSelectedStoreIds([]);
    refetch();
    // The change is now the newest record of the trail.
    queryClient.invalidateQueries([ENTITLEMENT_AUDIT_TRAIL_QUERY_KEY, businessId]);
    setChangeCount((count) => count + 1);
  };

  const { mutate: grant, isLoading: isGranting } = useGrantEntitlements({
    successCallback: handleSuccess,
  });
  const { mutate: revoke, isLoading: isRevoking } = useRevokeEntitlements({
    successCallback: handleSuccess,
  });

  const handleConfirm = ({ packageKey, slugs, reason }: EntitlementChange): void => {
    const storeIds = selectedRows.map(({ id }) => id);

    if (action === ENTITLEMENT_ACTIONS.GRANT && packageKey) {
      grant({ businessId, storeIds, packageKey, reason });
    } else if (action === ENTITLEMENT_ACTIONS.REVOKE && slugs) {
      revoke({ businessId, storeIds, slugs, reason });
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ my: 2 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedRows.length}
          onClick={() => setAction(ENTITLEMENT_ACTIONS.GRANT)}
        >
          Grant
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={!hasRevocableSelection}
          onClick={() => setAction(ENTITLEMENT_ACTIONS.REVOKE)}
        >
          Revoke
        </Button>
      </Stack>

      <CentsDataGrid
        tableName="ENTITLEMENTS_FOR_INDIVIDUAL_BUSINESS"
        data={rows}
        columns={COLUMNS}
        isLoading={isLoading}
        checkboxSelection
        rowSelectionModel={selectedStoreIds}
        onRowSelectionModelChange={setSelectedStoreIds}
      />

      {action && (
        <EntitlementChangeDialog
          open
          action={action}
          stores={selectedRows}
          isSubmitting={isGranting || isRevoking}
          onCancel={() => setAction(null)}
          onConfirm={handleConfirm}
        />
      )}

      <EntitlementAuditTrail businessId={businessId} stores={stores} changeCount={changeCount} />
    </Box>
  );
};
